/**
 * React hook for in-memory document state management
 * 
 * This implements the RFC vision where in-memory state is the source of truth
 * and provides real-time UI updates for both local and sync changes
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { DocumentStateManagerRFC, DocumentStateRFC, UIDocumentObject } from '../services/DocumentStateManagerRFC';
import { InMemorySourceOfTruth } from '../services/InMemorySourceOfTruth';
import { DocumentPersistenceManager } from '../services/DocumentPersistenceManager';
import { ObjectProperty } from '../types/shared-types';
import { clientStateManager } from '../services/ClientStateManager';

interface UseDocumentStateOptions {
  documentId: string;
  sqliteService: any;
  onSyncChanges?: (properties: ObjectProperty[]) => void;
  onContentSaved?: (objectId: string, changedProperties: string[]) => void;
}

interface UseDocumentStateReturn {
  // State
  documentState: DocumentStateRFC;
  pages: UIDocumentObject[];
  isLoading: boolean;
  error: string | null;
  
  // Actions
  loadDocument: () => Promise<void>;
  applySyncChanges: (properties: ObjectProperty[]) => void;
  updateProperty: (objectId: string, property: string, value: any) => void;
  createPage: (title: string) => Promise<string>;
  createTextBlock: (pageId: string, text: string) => Promise<string>;
  saveDocument: () => Promise<void>;
  
  // Computed
  getTextBlocks: (pageId: string) => UIDocumentObject[];
  
  // Manager access (for sync engine)
  manager: DocumentStateManagerRFC | null;
  sourceOfTruth: InMemorySourceOfTruth | null;
}

export function useDocumentState({ 
  documentId, 
  sqliteService, 
  onSyncChanges,
  onContentSaved
}: UseDocumentStateOptions): UseDocumentStateReturn {
  
  const stateManagerRef = useRef<DocumentStateManagerRFC | null>(null);
  const sourceOfTruthRef = useRef<InMemorySourceOfTruth | null>(null);
  const persistenceManagerRef = useRef<DocumentPersistenceManager | null>(null);
  
  const [documentState, setDocumentState] = useState<DocumentStateRFC>({
    documentId,
    title: documentId,
    objects: new Map(),
    pages: [],
    isDirty: false,
    isLoaded: false
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize state manager
  useEffect(() => {
    if (!sqliteService) return;

    
    // Initialize RFC components
    const sourceOfTruth = new InMemorySourceOfTruth(documentId);
    const persistenceManager = new DocumentPersistenceManager(sourceOfTruth, sqliteService, documentId);
    const stateManager = new DocumentStateManagerRFC(
      documentId,
      sourceOfTruth,
      persistenceManager,
      sqliteService
    );
    
    // Subscribe to state changes
    const unsubscribe = stateManager.subscribe((newState) => {
      setDocumentState(newState);
    });

    // Store references
    sourceOfTruthRef.current = sourceOfTruth;
    persistenceManagerRef.current = persistenceManager;
    stateManagerRef.current = stateManager;

    return () => {
      unsubscribe();
      stateManagerRef.current = null;
      sourceOfTruthRef.current = null;
      persistenceManagerRef.current = null;
    };
  }, [documentId, sqliteService]);

  // Load document from database (initial load)
  const loadDocument = useCallback(async () => {
    if (!stateManagerRef.current) {
      console.warn('State manager not initialized');
      return;
    }

    if (documentState.isLoaded) {
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await stateManagerRef.current.loadFromDatabase();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load document';
      console.error('Failed to load document:', err);
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [documentId, documentState.isLoaded]);

  // Apply sync changes to in-memory state
  const applySyncChanges = useCallback((properties: ObjectProperty[]) => {
    if (!sourceOfTruthRef.current) {
      console.warn('Cannot apply sync changes - source of truth not initialized');
      return;
    }

    
    // Add each property to the source of truth
    for (const prop of properties) {
      sourceOfTruthRef.current.addProperty({
        objectId: prop.object_id,
        propertyName: prop.property,
        value: prop.value,
        lwwCounter: prop.lww_counter,
        lwwRandom: prop.lww_random,
        replicaId: prop.replica_id,
        logicalTimestamp: prop.logical_timestamp,
        source: 'remote',
        addedToMemoryAt: Date.now()
      });
    }
    
    // Refresh the UI state
    stateManagerRef.current?.refreshState();
    
    // Notify parent component if callback provided
    onSyncChanges?.(properties);
  }, [onSyncChanges]);

  // Update a property locally
  const updateProperty = useCallback((objectId: string, property: string, value: any) => {
    if (!stateManagerRef.current) {
      console.warn('Cannot update property - state manager not initialized');
      return;
    }

    stateManagerRef.current.updateProperty(objectId, property, value);
  }, []);

  // Create a new page
  const createPage = useCallback(async (title: string): Promise<string> => {
    if (!stateManagerRef.current) {
      throw new Error('State manager not initialized');
    }

    const pageId = `${documentId}-page-${Date.now()}`;
    
    // Calculate fractional position for new page
    const currentState = stateManagerRef.current.getState();
    const existingPages = Array.from(currentState.objects.values())
      .filter(obj => obj.parent_id === documentId && obj.object_type === 'page' && !obj.is_tombstoned)
      .map(obj => {
        const positionProp = obj.properties.get('position');
        return parseFloat(positionProp?.value || '0') || 0;
      })
      .sort((a, b) => a - b);
    
    // Generate fractional position: add after the last page
    const lastPosition = existingPages.length > 0 ? existingPages[existingPages.length - 1] : 0;
    const position = (lastPosition + 1).toString();


    // Create page object in memory (includes object.metadata property)
    stateManagerRef.current.createObject(pageId, 'page', documentId);

    // Add page properties
    updateProperty(pageId, 'title', title);
    updateProperty(pageId, 'position', position);

    return pageId;
  }, [documentId, updateProperty]);

  // Create a new text block
  const createTextBlock = useCallback(async (pageId: string, text: string): Promise<string> => {
    if (!stateManagerRef.current) {
      throw new Error('State manager not initialized');
    }

    const blockId = `${pageId}-block-${Date.now()}`;
    
    // Calculate fractional position for new text block
    const currentState = stateManagerRef.current.getState();
    const existingBlocks = Array.from(currentState.objects.values())
      .filter(obj => obj.parent_id === pageId && obj.object_type === 'text_block' && !obj.is_tombstoned)
      .map(obj => {
        const positionProp = obj.properties.get('position');
        return parseFloat(positionProp?.value || '0') || 0;
      })
      .sort((a, b) => a - b);
    
    // Generate fractional position: add after the last block
    const lastPosition = existingBlocks.length > 0 ? existingBlocks[existingBlocks.length - 1] : 0;
    const position = (lastPosition + 1).toString();


    // Create text block object in memory (includes object.metadata property)
    stateManagerRef.current.createObject(blockId, 'text_block', pageId);

    // Add text block properties
    updateProperty(blockId, 'text', text);
    updateProperty(blockId, 'position', position);

    return blockId;
  }, [updateProperty]);

  // Save document to database
  const saveDocument = useCallback(async () => {
    if (!persistenceManagerRef.current) {
      console.warn('Cannot save document - persistence manager not initialized');
      return;
    }

    try {
      const result = await persistenceManagerRef.current.persistPendingProperties();
      
      // Mark the document as clean after successful save
      stateManagerRef.current?.markClean();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save document';
      console.error('Failed to save document:', err);
      setError(errorMessage);
      throw err;
    }
  }, [documentId]);

  // Get text blocks for a page (computed from in-memory state)
  const getTextBlocks = useCallback((pageId: string): UIDocumentObject[] => {
    if (!stateManagerRef.current) {
      return [];
    }
    // Use getChildren to get text blocks (children of the page)
    return stateManagerRef.current.getChildren(pageId)
      .filter(obj => obj.object_type === 'text_block');
  }, []);

  // Compute pages from current state (RFC version has pages as computed property)
  const pages = documentState.pages;

  return {
    // State
    documentState,
    pages,
    isLoading,
    error,
    
    // Actions
    loadDocument,
    applySyncChanges,
    updateProperty,
    createPage,
    createTextBlock,
    saveDocument,
    
    // Computed
    getTextBlocks,
    
    // Manager access (for sync engine)
    manager: stateManagerRef.current,
    sourceOfTruth: sourceOfTruthRef.current
  };
}
